import React, { useState, useRef, useEffect } from "react";
import {
  Button,
  Box,
  CircularProgress,
  Popper,
  Grow,
  Paper,
  ClickAwayListener,
  MenuList,
  MenuItem,
  IconButton,
  Stack
} from "@mui/material";
import { ArrowDropDown, Search } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { searchPolicyAction } from "../redux/actions/dashboardAction";
import { clearGpId } from "../redux/actions/generalPartyInfoActions";

const MainMenu = () => {
  const [anchorEl, setAnchorEl] = useState({
    quote: null,
    admin: null,
    report: null,
    leads: null
  });
  const [isLoading, setIsLoading] = useState(true);
  
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const user = useSelector((state) => state?.user?.user);
  const dynamicTheme = useSelector((state) => state?.theme?.theme[0]);

  // Refs for each menu
  const quoteRef = useRef(null);
  const adminRef = useRef(null);
  const reportRef = useRef(null);
  const leadsRef = useRef(null);

  useEffect(() => {
    if (user) {
      setIsLoading(false);
    }
  }, [user]);

  const roleAssigned = user?.newRole || [];

  const hasSuperRole = roleAssigned.includes("ROLE_SUPER");
  const hasCorporateRole = roleAssigned.includes("ROLE_CORPORATE");
  const hasAuditorRole = roleAssigned.includes("ROLE_AUDITOR");
  const isEndUserOnly = roleAssigned.includes("ROLE_END_USER");

  const showManageCarrier = hasSuperRole || hasCorporateRole;
  const showCarrierGroup = hasSuperRole || hasCorporateRole;
  const showBotMenu = !hasAuditorRole && !isEndUserOnly;
  const showReportMenu = !hasAuditorRole && !isEndUserOnly;
  const showTaskManagement = !hasAuditorRole;
  const showDashboardMenu = !hasAuditorRole && !isEndUserOnly;
  const showLeadsMenu = !hasAuditorRole && !isEndUserOnly;
  const showSubMenu = !hasAuditorRole;
  const showAdminMenu = roleAssigned.length > 0 && !hasAuditorRole && !isEndUserOnly;

const handleToggle = (menuType) => (event) => {
  event.preventDefault();
  event.stopPropagation();

  setAnchorEl((prev) => {
    const isSameMenuOpen = Boolean(prev[menuType]);
    // Close all menus first
    const newState = {
      quote: null,
      admin: null,
      report: null,
      leads: null,
    };
    // If same menu was not open, open it now
    if (!isSameMenuOpen) {
      newState[menuType] = event.currentTarget;
    }
    return newState;
  });
};


  const handleClose = (menuType) => (event) => {
    if (
      (menuType === 'quote' && quoteRef.current && quoteRef.current.contains(event.target)) ||
      (menuType === 'admin' && adminRef.current && adminRef.current.contains(event.target)) ||
      (menuType === 'report' && reportRef.current && reportRef.current.contains(event.target)) ||
      (menuType === 'leads' && leadsRef.current && leadsRef.current.contains(event.target))
    ) {
      return;
    }
    setAnchorEl(prev => ({ ...prev, [menuType]: null }));
  };
 const handleCreatePolicyClick = (event) => {
    event.preventDefault();
   handleClose();
    navigateWithGuard('/createpolicy', { askToReset: true, fromMenu: true });
  };

  const handleListKeyDown = (menuType) => (event) => {
    if (event.key === 'Tab') {
      event.preventDefault();
      setAnchorEl(prev => ({ ...prev, [menuType]: null }));
    } else if (event.key === 'Escape') {
      setAnchorEl(prev => ({ ...prev, [menuType]: null }));
    }
  };

  // Return focus to the button when menu closes
  const prevOpen = useRef(anchorEl);
  useEffect(() => {
    Object.keys(anchorEl).forEach(menuType => {
      if (prevOpen.current[menuType] === true && anchorEl[menuType] === null) {
        const refMap = {
          quote: quoteRef,
          admin: adminRef,
          report: reportRef,
          leads: leadsRef
        };
        if (refMap[menuType]?.current) {
          refMap[menuType].current.focus();
        }
      }
    });
    prevOpen.current = anchorEl;
  }, [anchorEl]);

  const triggerCreateQuoteIntent = () => {
    let cancelled = false;

    const cancel = () => { cancelled = true; };
    const proceed = () => {
      dispatch(clearGpId());
      dispatch({ type: "RESET_GENERALPARTYINFO" });
      dispatch({ type: "RESET_ALL_VEHICLE_INFO" });
      dispatch({ type: "RESET_ALL_DRIVER_INFO" });
      dispatch({ type: "RESET_ALL_RATES" });

      navigate("/", { state: { isNewQuote: true, navToken: Date.now() } });
    };

    window.dispatchEvent(
      new CustomEvent("app:create-quote-intent", { 
        detail: { cancel, proceed, origin: 'mainMenu' } 
      })
    );

    if (!cancelled) proceed();
  };

  const handleNewQuoteClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setAnchorEl(prev => ({ ...prev, quote: null }));
    triggerCreateQuoteIntent();
  };

  const navigateWithGuard = (path, state = {}) => {
    let cancelled = false;

    const cancel = () => { cancelled = true; };
    const proceed = () => { navigate(path, { state }); };

    window.dispatchEvent(
      new CustomEvent('app:navigation-intent', {
        detail: { path, state, cancel, proceed, origin: 'mainMenu' }
      })
    );

    if (!cancelled) {
      proceed();
    }
  };

  const handleNavigation = (path, needsReload = false, state = {}) => (event) => {
 event.preventDefault();
  dispatch(clearGpId());
  handleClose();

    if (path === "/managepolicylisting") {
      dispatch(searchPolicyAction());
    }

    const finalState = {
      ...state,
      navTimestamp: Date.now()
    };

    navigateWithGuard(path, finalState);
  };

 

  const handleMenuItemClick = (menuType, callback) => (event) => {
    event.preventDefault();
    event.stopPropagation();
    setAnchorEl(prev => ({ ...prev, [menuType]: null }));
    if (callback) {
      callback(event);
    }
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          padding: "12px"
        }}
      >
        <CircularProgress size={24} />
      </Box>
    );
  }

  const renderMenu = (menuType, buttonRef, buttonContent, menuItems) => (
    <Box sx={{ display: 'inline-block' }}>
      <Button
        ref={buttonRef}
        aria-controls={anchorEl[menuType] ? `${menuType}-menu` : undefined}
        aria-expanded={anchorEl[menuType] ? 'true' : undefined}
        aria-haspopup="true"
        onClick={handleToggle(menuType)}
        endIcon={<ArrowDropDown />}
        sx={{ 
          margin: "0 12px", 
          fontFamily: '"Poppins", sans-serif',
          textTransform: 'none', 
          fontSize: '16px'
        }}
        style={{ color: dynamicTheme?.secondaryColor }}
      >
        {buttonContent}
      </Button>
      <Popper
        open={Boolean(anchorEl[menuType])}
        anchorEl={anchorEl[menuType]}
        role={undefined}
        placement="bottom-start"
        transition
        disablePortal
        style={{ zIndex: 1300 }}
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === 'bottom-start' ? 'left top' : 'left bottom',
            }}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleClose(menuType)}>
                <MenuList
                  autoFocusItem={Boolean(anchorEl[menuType])}
                  id={`${menuType}-menu`}
                  aria-labelledby={`${menuType}-button`}
                  onKeyDown={handleListKeyDown(menuType)}
                  sx={{ fontFamily: '"Poppins", sans-serif' }}
                >
                  {menuItems}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </Box>
  );

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexWrap: "wrap",
        width: "auto",
        maxWidth: "1200px",
        flexShrink: 0,
        margin: "auto",
        padding: "10px 0px",
      }}
    >
      {!hasAuditorRole && (
        <IconButton
          onClick={handleNavigation("/globalsearch", false)}
          aria-label="Search"
          sx={{ color: "#000000", marginLeft: "30px" }}
        >
          <Search sx={{ width: 25, height: 22.8 }} />
        </IconButton>
      )}

      {showDashboardMenu && (
        <Button
          sx={{ 
            margin: "0 12px", 
            fontFamily: '"Poppins", sans-serif',
            textTransform: 'none', 
            fontSize: '16px'  
          }}
          style={{ color: dynamicTheme?.secondaryColor }}
          onClick={handleNavigation("/home")}
        >
          Dashboard
        </Button>
      )}

      {showLeadsMenu && renderMenu(
        'leads',
        leadsRef,
        'Leads',
        showSubMenu ? [
          <MenuItem
            key="createLead"
            onClick={handleMenuItemClick('leads', () => {
              navigateWithGuard('/createlead', { reset: Date.now() });
            })}
            sx={{fontFamily: "Poppins, sans-serif"}}
            style={{ color: dynamicTheme?.secondaryColor }}
          >
            Create Lead
          </MenuItem>,
          <MenuItem
            key="allLeads"
            onClick={handleMenuItemClick('leads', handleNavigation("/allleads", false, { openCreateCustomer: true }))}
            style={{ color: dynamicTheme?.secondaryColor }}
            sx={{fontFamily: "Poppins, sans-serif"}}
          >
            All Leads
          </MenuItem>
        ] : []
      )}

      {showBotMenu && (
        <Button
          sx={{ 
            margin: "0 12px", 
            fontFamily: '"Poppins", sans-serif',
            fontSize: '16px' 
          }}
          style={{ color: dynamicTheme?.secondaryColor }}
          onClick={handleNavigation("/signupbot")}
        >
          Bot
        </Button>
      )}

      {renderMenu(
        'quote',
        quoteRef,
        'CRM',
        [
          ...(showSubMenu ? [
            <MenuItem
              key="searchCustomer"
              onClick={handleMenuItemClick('quote', handleNavigation("/globalsearch"))}
              style={{ color: dynamicTheme?.secondaryColor }}
              sx={{fontFamily: "Poppins, sans-serif"}}
            >
              Search Customer
            </MenuItem>,
            <MenuItem
              key="createCustomer"
              onClick={handleMenuItemClick('quote', handleNavigation("/globalsearch", false, { 
                openCreateCustomer: true, 
                navTimestamp: Date.now() 
              }))}
              style={{ color: dynamicTheme?.secondaryColor }}
              sx={{fontFamily: "Poppins, sans-serif"}}
            >
              Create Customer
            </MenuItem>,
            <MenuItem
              key="createPolicy"
              onClick={handleCreatePolicyClick}
              style={{ color: dynamicTheme?.secondaryColor }}
              sx={{fontFamily: "Poppins, sans-serif"}}
            >
              Create Policy
            </MenuItem>,
            <MenuItem
              key="searchPolicy"
              onClick={handleMenuItemClick('quote', handleNavigation("/policylist"))}
              style={{ color: dynamicTheme?.secondaryColor }}
              sx={{fontFamily: "Poppins, sans-serif"}}
            >
              Search Policy
            </MenuItem>
          ] : []),
          <MenuItem
            key="createQuote"
            onClick={handleNewQuoteClick}
            style={{ color: dynamicTheme?.secondaryColor }}
            sx={{fontFamily: "Poppins, sans-serif"}}
          >
            Create Quote
          </MenuItem>,
          <MenuItem
            key="searchQuote"
            onClick={handleMenuItemClick('quote', handleNavigation("/searchsavedquote", true))}
            style={{ color: dynamicTheme?.secondaryColor }}
            sx={{fontFamily: "Poppins, sans-serif"}}
          >
            Search Quote
          </MenuItem>
        ]
      )}

      {showTaskManagement && (
        <Button
          sx={{ 
            margin: "0 12px", 
            fontFamily: '"Poppins", sans-serif',
            textTransform: 'none', 
            fontSize: '16px' 
          }}
          style={{ color: dynamicTheme?.secondaryColor }}
          onClick={handleNavigation("/taskmanagment")}
        >
          Task Management
        </Button>
      )}

      {showReportMenu && renderMenu(
        'report',
        reportRef,
        'Report',
        [
          <MenuItem
            key="botActivity"
            onClick={handleMenuItemClick('report', handleNavigation("/botreport"))}
            style={{ color: dynamicTheme?.secondaryColor }}
            sx={{fontFamily: "Poppins, sans-serif"}}
          >
            Bot Activity Log Report
          </MenuItem>,
          <MenuItem
            key="historyLog"
            onClick={handleMenuItemClick('report', handleNavigation("/botallreport"))}
            style={{ color: dynamicTheme?.secondaryColor }}
            sx={{fontFamily: "Poppins, sans-serif"}}
          >
            History Log Report
          </MenuItem>,
          <MenuItem
            key="productionReport"
            onClick={handleMenuItemClick('report', handleNavigation("/leadsourcereport"))}
            style={{ color: dynamicTheme?.secondaryColor }}
            sx={{fontFamily: "Poppins, sans-serif"}}
          >
            Production Report
          </MenuItem>,
          <MenuItem
            key="customerCrm"
            onClick={handleMenuItemClick('report', handleNavigation("/customercrmlead"))}
            style={{ color: dynamicTheme?.secondaryColor }}
            sx={{fontFamily: "Poppins, sans-serif"}}
          >
            Customer Crm Lead Report
          </MenuItem>,
          <MenuItem
            key="taskReport"
            onClick={handleMenuItemClick('report', handleNavigation("/taskreport"))}
            style={{ color: dynamicTheme?.secondaryColor }}
            sx={{fontFamily: "Poppins, sans-serif"}}
          >
            Task Report
          </MenuItem>,
          <MenuItem
            key="employeeLog"
            onClick={handleMenuItemClick('report', handleNavigation("/lastloginreport"))}
            style={{ color: dynamicTheme?.secondaryColor }}
            sx={{fontFamily: "Poppins, sans-serif"}}
          >
            Employee Log Report
          </MenuItem>,
          <MenuItem
            key="raterReport"
            onClick={handleMenuItemClick('report', handleNavigation("/raterreport"))}
            style={{ color: dynamicTheme?.secondaryColor }}
            sx={{fontFamily: "Poppins, sans-serif"}}
          >
            Rater Report
          </MenuItem>
        ]
      )}

      {showAdminMenu && renderMenu(
        'admin',
        adminRef,
        'Admin',
        [
          <MenuItem 
            key="locationList"
            onClick={handleMenuItemClick('admin', handleNavigation("/locationmanager"))}
            style={{ color: dynamicTheme?.secondaryColor }}
          >
            Location List
          </MenuItem>,
          <MenuItem 
            key="locationGroup"
            onClick={handleMenuItemClick('admin', handleNavigation("/locationgrp"))}
            style={{ color: dynamicTheme?.secondaryColor }}
          >
            Location Group
          </MenuItem>,
          ...(showManageCarrier ? [
            <MenuItem 
              key="manageCarrier"
              onClick={handleMenuItemClick('admin', handleNavigation("/managecarrier"))}
              style={{ color: dynamicTheme?.secondaryColor }}
            >
              Manage Carrier
            </MenuItem>
          ] : []),
          ...(showCarrierGroup ? [
            <MenuItem 
              key="carrierGroup"
              onClick={handleMenuItemClick('admin', handleNavigation("/carriergroup"))}
              style={{ color: dynamicTheme?.secondaryColor }}
            >
              Carrier Group
            </MenuItem>
          ] : []),
          <MenuItem 
            key="userList"
            onClick={handleMenuItemClick('admin', handleNavigation("/userlist"))}
            style={{ color: dynamicTheme?.secondaryColor }}
          >
            User List
          </MenuItem>,
          <MenuItem 
            key="templates"
            onClick={handleMenuItemClick('admin', handleNavigation("/template"))}
            style={{ color: dynamicTheme?.secondaryColor }}
          >
            Templates
          </MenuItem>
        ]
      )}
    </Box>
  );
};

export default MainMenu;